import React, { JSX, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { favoritesService } from "../services/favoritesService";
import { fetchShirtsFromApi, fetchShirtById } from "../services/shirtService";
import { auctionService, Auction } from "../services/auctionService";
import { ApiShirt } from "../types/Shirt";
import { hasActiveAuction } from "../utils/auctionVisibility";

import Hero from "../components/layout/Hero";
import SubnavLinks from "../components/layout/SubnavLinks";
import ShirtsCarousel from "../components/Catalog/ShirtsCarousel";
import TrustpilotReviewCollector from "../components/trustpilot/Trustpilotreviewcollector";

import "./Homepage.css";

function SkeletonRow() {
  return (
    <div className="homepage-skeleton-row">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="homepage-skeleton-card" />
      ))}
    </div>
  );
}

function dedupeShirtsById(shirts: ApiShirt[]): ApiShirt[] {
  return Array.from(new Map(shirts.map((shirt) => [shirt.id, shirt])).values());
}

function getCreatedAtTimestamp(createdAt: string): number {
  const timestamp = Date.parse(createdAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export default function HomePage(): JSX.Element {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { t } = useTranslation();

  const [allShirts, setAllShirts] = useState<ApiShirt[]>([]);
  const [shirtsLoading, setShirtsLoading] = useState(true);
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [likedShirts, setLikedShirts] = useState<ApiShirt[]>([]);
  const [likedLoading, setLikedLoading] = useState(false);

  useEffect(() => {
    // Fetch shirts and auctions in parallel instead of sequentially
    const shirtsPromise = fetchShirtsFromApi().catch(() => [] as ApiShirt[]);
    const auctionsPromise = auctionService.listAuctions().catch(() => [] as Auction[]);

    Promise.all([shirtsPromise, auctionsPromise]).then(
      async ([shirts, auctionList]) => {
        const deduped = dedupeShirtsById(shirts);
        setAllShirts(deduped);
        setAuctions(auctionList);
        setShirtsLoading(false);

        // Fetch missing auction shirts in background
        const knownIds = new Set(deduped.map((s) => s.id));
        const missingIds = Array.from(
          new Set(
            auctionList
              .map((a) => a.shirt_id)
              .filter((shirtId) => !knownIds.has(shirtId)),
          ),
        );
        if (missingIds.length > 0) {
          const extra = await Promise.all(
            missingIds.map((shirtId) =>
              fetchShirtById(shirtId).catch(() => null),
            ),
          );
          const valid = extra.filter((s): s is ApiShirt => s !== null);
          if (valid.length > 0) {
            setAllShirts((prev) => dedupeShirtsById([...prev, ...valid]));
          }
        }
      },
    );
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      setLikedShirts([]);
      setLikedLoading(false);
      return;
    }

    setLikedLoading(true);
    favoritesService
      .getLikedShirts()
      .then((shirts) => setLikedShirts(shirts))
      .catch(() => setLikedShirts([]))
      .finally(() => setLikedLoading(false));
  }, [isAuthed]);

  /** Map shirt_id → Auction for quick lookup */
  const auctionByShirtId = useMemo(() => {
    const map = new Map<string, Auction>();
    const nowMs = Date.now();
    for (const a of auctions) {
      const parsed = Date.parse(a.end_time);
      if (Number.isFinite(parsed) && parsed > nowMs) {
        map.set(a.shirt_id, a);
      }
    }
    return map;
  }, [auctions]);

  const auctionShirts = useMemo(
    () =>
      allShirts
        .filter((s) => hasActiveAuction(s, auctionByShirtId.get(s.id)))
        .sort((a, b) => {
          const aEnd = auctionByShirtId.get(a.id)?.end_time ?? a.auction_end_time;
          const bEnd = auctionByShirtId.get(b.id)?.end_time ?? b.auction_end_time;
          const aTime = aEnd ? new Date(aEnd).getTime() : Infinity;
          const bTime = bEnd ? new Date(bEnd).getTime() : Infinity;
          return aTime - bTime;
        }),
    [allShirts, auctionByShirtId],
  );
  const fixedShirts = useMemo(
    () =>
      allShirts.filter(
        (s) => s.sales_type === "fixed_price" && !auctionByShirtId.has(s.id),
      ),
    [allShirts, auctionByShirtId],
  );
  const popularShirts = useMemo(
    () =>
      allShirts
        .filter((s) => s.is_for_sale && s.approval_status === "approved")
        .sort((a, b) => (b.likes_count ?? 0) - (a.likes_count ?? 0)),
    [allShirts],
  );

  const newShirts = useMemo(
    () =>
      [...allShirts].sort((a, b) => {
        const createdAtDiff =
          getCreatedAtTimestamp(b.created_at) -
          getCreatedAtTimestamp(a.created_at);
        if (createdAtDiff !== 0) return createdAtDiff;
        return b.id.localeCompare(a.id);
      }),
    [allShirts],
  );
  const builtByCollectorsLines = t("home.builtByCollectorsText").split("\n");

  return (
    <>
      <a className="skip-link" href="#main">
        {t("home.skipToContent")}
      </a>

      <main id="main" className="site-main">
        <SubnavLinks />
        <Hero />

        <section className="homepage-section" aria-label={t("home.endingSoon")}>
          <div className="homepage-shell">
            <h2
  className="homepage-title homepage-title--with-dot homepage-title--clickable"
  onClick={() => navigate("/catalog?type=ending-soon")}
  onKeyDown={(e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate("/catalog?type=ending-soon");
    }
  }}
  role="link"
  tabIndex={0}
  aria-label={t("home.endingSoon")}
>
  <span className="homepage-live-dot" />
  <span>{t("home.endingSoon")}</span>
</h2>

            {shirtsLoading ? (
              <SkeletonRow />
            ) : (
              <ShirtsCarousel
                apiShirts={auctionShirts}
                auctionMap={auctionByShirtId}
                imageVariant="thumbnail"
                label={t("home.endingSoon")}
                gridId="endingSoonCarousel"
                endCtaLabel={t("home.discoverShirts")}
                onEndCtaClick={() => navigate("/catalog")}
              />
            )}
          </div>
        </section>

        <section
          className="homepage-section"
          aria-label={t("home.popularShirts")}
        >
          <div className="homepage-shell">
            <h2 className="homepage-title">{t("home.popularShirts")}</h2>

            {shirtsLoading ? (
              <SkeletonRow />
            ) : (
              <ShirtsCarousel
                apiShirts={popularShirts}
                auctionMap={auctionByShirtId}
                imageVariant="thumbnail"
                label={t("home.popularShirts")}
                gridId="popularCarousel"
                showSeeMoreButton={true}
                onSeeMore={() => navigate("/catalog")}
              />
            )}
          </div>
        </section>

        <section className="homepage-section" aria-label={t("home.newShirts")}>
          <div className="homepage-shell">
            <h2 className="homepage-title">{t("home.newShirts")}</h2>

            {shirtsLoading ? (
              <SkeletonRow />
            ) : (
              <ShirtsCarousel
                apiShirts={newShirts}
                auctionMap={auctionByShirtId}
                imageVariant="thumbnail"
                label={t("home.newShirts")}
                gridId="newCarousel"
                showSeeMoreButton={true}
                onSeeMore={() => navigate("/catalog")}
              />
            )}
          </div>
        </section>

        {isAuthed && (
          <section
            className="homepage-section"
            aria-label={t("home.myFavorites")}
          >
            <div className="homepage-shell">
              <h2 className="homepage-title">{t("home.myFavorites")}</h2>

              {likedLoading ? (
                <p>{t("favorites.loading", "Favorieten laden...")}</p>
              ) : likedShirts.length === 0 ? (
                <p>
                  {t("favorites.noFavorites", "Je hebt nog geen favorieten")}
                </p>
              ) : (
                <ShirtsCarousel
                  apiShirts={likedShirts}
                  auctionMap={auctionByShirtId}
                  imageVariant="thumbnail"
                  label={t("home.myFavorites")}
                  gridId="favoritesCarousel"
                  showSeeMoreButton={true}
                  onSeeMore={() => navigate("/favorites")}
                />
              )}
            </div>
          </section>
        )}

        <section
          className="homepage-section"
          aria-label={t("home.builtByCollectors")}
        >
          <div className="homepage-shell">
            <div className="built-card">
              <div className="built-card__content">
                <h2 className="built-card__title">
                  {t("home.builtByCollectors")}
                </h2>

                <p className="built-card__text">
                  {builtByCollectorsLines.map((line, index) => (
                    <React.Fragment key={`${line}-${index}`}>
                      {line}
                      {index < builtByCollectorsLines.length - 1 && (
                        <>
                          <br />
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </p>

                <button
                  className="built-card__button"
                  type="button"
                  onClick={() => navigate("/about")}
                >
                  {t("home.readOurStory")}
                </button>
              </div>

              <div className="built-card__visual">
                <img
                  src="/images/banners/aboutme_banner.jpg"
                  alt={t("home.communityImageAlt")}
                  className="built-card__image"
                />
              </div>
            </div>
          </div>
        </section>

        <section
          className="homepage-section homepage-section--workflow"
          aria-label={t("home.howItWorks")}
        >
          <div className="homepage-shell">
            <div className="workflow-grid">
              <div className="workflow-column">
                <div className="workflow-heading-row">
                  <span className="workflow-line" />
                  <h3 className="workflow-heading">{t("home.forBuyers")}</h3>
                  <span className="workflow-line" />
                </div>

                <div className="workflow-card">
                  <div className="workflow-step">
                    <div className="workflow-step-top">
                      <div className="workflow-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="11" cy="11" r="7" />
                          <line x1="16.5" y1="16.5" x2="22" y2="22" />
                        </svg>
                      </div>
                      <h4 className="workflow-step-title">
                        {t("home.discoverShirts")}
                      </h4>
                      <p className="workflow-step-text">
                        {t("home.discoverShirtsText")}
                      </p>
                    </div>

                    <button
                      className="workflow-button"
                      type="button"
                      onClick={() => navigate("/catalog")}
                    >
                      {t("common.buyNow")}
                    </button>
                  </div>

                  <div className="workflow-divider" />

                  <div className="workflow-step">
                    <div className="workflow-step-top">
                      <div className="workflow-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          {/* gavel head */}
                          <path d="M14 4 L20 10 L17 13 L11 7 Z" />
                          {/* gavel handle */}
                          <line x1="14" y1="11" x2="6" y2="19" strokeWidth="2.8" strokeLinecap="round" />
                          {/* base */}
                          <line x1="3" y1="21" x2="13" y2="21" />
                        </svg>
                      </div>
                      <h4 className="workflow-step-title">{t("home.bid")}</h4>
                      <p className="workflow-step-text">{t("home.bidText")}</p>
                    </div>

                    <button
                      className="workflow-button"
                      type="button"
                      onClick={() => navigate("/catalog?type=auction")}
                    >
                      {t("common.bidNow")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="workflow-column">
                <div className="workflow-heading-row">
                  <span className="workflow-line" />
                  <h3 className="workflow-heading">{t("home.forSellers")}</h3>
                  <span className="workflow-line" />
                </div>

                <div className="workflow-card">
                  <div className="workflow-step">
                    <div className="workflow-step-top">
                      <div className="workflow-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M12 16V4" />
                          <path d="m8 8 4-4 4 4" />
                          <path d="M4 20h16" />
                        </svg>
                      </div>
                      <h4 className="workflow-step-title">
                        {t("home.uploadYourShirt")}
                      </h4>
                      <p className="workflow-step-text">
                        {t("home.uploadYourShirtText")}
                      </p>
                    </div>

                    <button
                      className="workflow-button"
                      type="button"
                      onClick={() => navigate("/sell")}
                    >
                      {t("common.sellNow")}
                    </button>
                  </div>

                  <div className="workflow-divider" />

                  <div className="workflow-step workflow-step--verification">
                    <div className="workflow-step-top">
                      <div className="workflow-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <h4 className="workflow-step-title">
                        {t("home.verificationCheck")}
                      </h4>
                      <p className="workflow-step-text">
                        {t("home.verificationCheckText")}
                      </p>
                    </div>

                    <button
                      className="workflow-button"
                      type="button"
                      onClick={() => navigate("/info")}
                    >
                      {t("common.learnMore")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TrustpilotReviewCollector />
      </main>
    </>
  );
}
